import { useState, useEffect } from "react";
import { doesUserHavePackages, PossiblePackage } from "lhc_permissions";
import { useStytchMember } from "@stytch/react/b2b";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/components/ui/use-toast";
import { cn, GenerateLoadingOrError } from "@/lib/utils";
import { Flag, useListGeneralContractFlags, useUpdateGeneralContractFlag } from "@/lib/api/flags";
import { subscribeUser } from "@/lib/notifications/subscription";
import { Label } from "@/components/ui/label";
import { Loader } from "@/components/ui/loader";
import { Button } from "@/components/ui/button";
import { useHasNotificationSubscription, useListSubscriptions, useSubscribe, useUnsubscribe, useTestNotification, NotificationType, NotificationSubscription } from "@/lib/api/user_notification";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle, Bell, BellOff, Info, Settings2, Inbox } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

export function SettingsDashboard() {
  const { member } = useStytchMember();
  const hasGeneralContract = doesUserHavePackages({
    requiredPackages: [PossiblePackage.GeneralContract],
    member,
  });

  return (
    <div className="flex flex-col h-screen max-h-[calc(100vh-4rem)]">
      <div className="mb-4">
        <h1 className="scroll-m-20 text-2xl font-extrabold tracking-tight lg:text-3xl">
          Settings
        </h1>
      </div>
      
      <Tabs defaultValue="notifications" className="w-full">
        <TabsList className="grid w-full max-w-[400px] grid-cols-2">
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Notifications
          </TabsTrigger>
          {hasGeneralContract && (
            <TabsTrigger value="general" className="flex items-center gap-2">
              <Settings2 className="h-4 w-4" />
              Contracts
            </TabsTrigger>
          )}
        </TabsList>
        
        <TabsContent value="notifications" className="mt-4">
          <NotificationSettings />
        </TabsContent>
        
        {hasGeneralContract && (
          <TabsContent value="general" className="mt-4">
            <GeneralContractSettings />
          </TabsContent>
        )}
      </Tabs>
    </div>
  )
}

function NotificationSettings() {
  const { toast } = useToast();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const { member } = useStytchMember();
  const subscriptionDataResult = useHasNotificationSubscription(!hasSubmitted ? 1000 * 30 : 1000 * 3);
  const subscriptionsResult = useListSubscriptions();
  const subscribeMutation = useSubscribe();
  const unsubscribeMutation = useUnsubscribe();
  const testNotificationMutation = useTestNotification();

  const hasWebPushEnabled = subscriptionDataResult.data?.data?.data || false;

  const handleSubscribeWebPush = async () => {
    setHasSubmitted(true);
    try {
      await subscribeUser();
      toast({ title: "Success", description: "Web push notifications enabled" });
    } catch (error) {
      console.error('Failed to subscribe user to notifications', error);
      toast({ title: "Failed", description: "Failed to enable web push notifications", variant: "destructive" });
    }
  };

  const handleTestNotification = async (requireInteraction: boolean) => {
    try {
      const result = await testNotificationMutation.mutateAsync({ requireInteraction });
      if (result.success) {
        toast({ 
          title: "Test notification sent", 
          description: requireInteraction ? "Banner notification sent" : "Auto-dismiss notification sent" 
        });
      } else {
        throw new Error("Failed to send test notification");
      }
    } catch (error) {
      console.error('Failed to send test notification', error);
      toast({ 
        title: "Failed", 
        description: "Failed to send test notification", 
        variant: "destructive" 
      });
    }
  };

  const handleToggleSubscription = async (
    notificationType: NotificationType, 
    isSubscribed: boolean, 
    disableBannerAlert: boolean
  ) => {
    try {
      if (isSubscribed) {
        const result = await unsubscribeMutation.mutateAsync({ notification_type: notificationType });
        if (result.success) {
          toast({ title: "Unsubscribed", description: `Unsubscribed from ${notificationType} notifications` });
        }
      } else {
        const result = await subscribeMutation.mutateAsync({ 
          notification_type: notificationType, 
          disable_banner_alert: disableBannerAlert 
        });
        if (result.success) {
          toast({ title: "Subscribed", description: `Subscribed to ${notificationType} notifications` });
        }
      }
    } catch (error) {
      console.error('Failed to toggle subscription', error);
      toast({ 
        title: "Failed", 
        description: `Failed to ${isSubscribed ? 'unsubscribe from' : 'subscribe to'} notifications`, 
        variant: "destructive" 
      });
    }
  };

  const handleToggleBannerAlert = async (
    notificationType: NotificationType,
    currentDisableBanner: boolean
  ) => {
    try {
      const result = await subscribeMutation.mutateAsync({ 
        notification_type: notificationType, 
        disable_banner_alert: !currentDisableBanner 
      });
      if (result.success) {
        toast({ 
          title: "Updated", 
          description: currentDisableBanner ? "Banner alerts enabled" : "Banner alerts disabled" 
        });
      }
    } catch (error) {
      console.error('Failed to toggle banner alert', error);
      toast({ 
        title: "Failed", 
        description: "Failed to update banner alert preference", 
        variant: "destructive" 
      });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Desktop Notifications</h2>
        <p className="text-sm text-muted-foreground">
          Manage your notification preferences and test desktop notifications
        </p>
      </div>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Web Push Notifications</CardTitle>
          <CardDescription>
            Enable browser notifications to receive real-time alerts
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <p className="text-sm font-medium">Browser Notifications</p>
              <p className="text-xs text-muted-foreground">
                Receive notifications even when the app is not open
              </p>
            </div>
            {subscriptionDataResult.isLoading ? (
              <Skeleton className="w-20 h-10" />
            ) : hasWebPushEnabled ? (
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm font-medium">Enabled</span>
              </div>
            ) : (
              <Button onClick={handleSubscribeWebPush} size="sm">
                Enable
              </Button>
            )}
          </div>

          {hasWebPushEnabled && (
            <div className="mt-4 space-y-4">
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Test Your Notifications</AlertTitle>
                <AlertDescription>
                  Send test notifications to verify your settings are working correctly.
                </AlertDescription>
              </Alert>
              <div className="flex gap-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handleTestNotification(true)}
                  disabled={testNotificationMutation.isLoading}
                >
                  Test Banner Alert
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => handleTestNotification(false)}
                  disabled={testNotificationMutation.isLoading}
                >
                  Test Auto-dismiss
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {hasWebPushEnabled && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Notification Types</CardTitle>
            <CardDescription>
              Choose which types of notifications you want to receive
            </CardDescription>
          </CardHeader>
          <CardContent>

            {subscriptionsResult.isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="w-full h-16" />
                ))}
              </div>
            ) : subscriptionsResult.data?.data?.subscriptions?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <Inbox className="h-12 w-12 text-muted-foreground mb-4" />
                <p className="text-sm text-muted-foreground">
                  No notification types available
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {subscriptionsResult.data?.data?.subscriptions?.map((subscription: NotificationSubscription) => (
                  <div 
                    key={subscription.notification_type} 
                    className="flex items-start justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                  >
                    <div className="space-y-2 flex-1">
                      <div className="flex items-start justify-between">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Label className="text-sm font-medium">{subscription.name}</Label>
                            {(subscription.is_subscribed || subscription.notification_type === NotificationType.System) && (
                              <Bell className="h-3.5 w-3.5 text-green-600" />
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground pr-4">{subscription.description}</p>
                        </div>
                        <Switch
                          checked={subscription.notification_type === NotificationType.System ? true : subscription.is_subscribed}
                          onCheckedChange={() => handleToggleSubscription(
                            subscription.notification_type,
                            subscription.is_subscribed,
                            subscription.disable_banner_alert
                          )}
                          disabled={subscription.notification_type === NotificationType.System || subscribeMutation.isLoading || unsubscribeMutation.isLoading}
                        />
                      </div>
                      
                      {(subscription.is_subscribed || subscription.notification_type === NotificationType.System) && (
                        <div className="flex items-center gap-2 pl-0.5">
                          <Checkbox
                            id={`banner-${subscription.notification_type}`}
                            checked={!subscription.disable_banner_alert}
                            onCheckedChange={() => handleToggleBannerAlert(
                              subscription.notification_type,
                              subscription.disable_banner_alert
                            )}
                            disabled={subscribeMutation.isLoading || unsubscribeMutation.isLoading}
                          />
                          <Label 
                            htmlFor={`banner-${subscription.notification_type}`}
                            className="text-xs cursor-pointer text-muted-foreground"
                          >
                            Show banner alerts (requires click to dismiss)
                          </Label>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function GeneralContractSettings() {
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">General Contract Settings</h2>
        <p className="text-sm text-muted-foreground">
          Configure general contract synchronization options
        </p>
      </div>
      <GeneralContractFlags />
    </div>
  );
}

export function GeneralContractFlags() {
  const { member } = useStytchMember();
  const isAvailable = doesUserHavePackages({
    requiredPackages: [PossiblePackage.GeneralContract],
    member,
  });

  const flagsRes = useListGeneralContractFlags();
  const updateFlag = useUpdateGeneralContractFlag();
  const loadingOrErrorUi = GenerateLoadingOrError([flagsRes]);

  if (!isAvailable) {
    return (
      null
    );
  }

  if (loadingOrErrorUi) {
    return (loadingOrErrorUi);
  }

  const flagsList: Flag[] = flagsRes?.data?.data || [];

  return (
    <div className="space-y-4">
      {flagsList.map(flag => (<RenderFlag updateFlag={updateFlag} flag={flag} key={`Flag:${flag.id}`} />))}
    </div>
  );
}

function RenderFlag({ updateFlag, flag }: { flag: Flag; updateFlag: any }) {
  const { toast } = useToast();
  const [isUpdating, setIsUpdating] = useState(false);

  const onUpdateFlag = async (item: boolean) => {
    try {
      if (isUpdating) { return; }
      setIsUpdating(true);

      const res = await updateFlag({ flagId: flag.id, newStatus: item });
      if (!res.success) {
        throw res;
      }

      toast({
        title: "Updated",
        description: "",
      });
    } catch (error) {
      console.error("Failed to update flag", error)
      toast({
        title: "Failed to update",
        description: "",
      });
    } finally {
      setIsUpdating(false);
    }

  }

  return (
    <div className="flex items-center justify-between p-4 border rounded-lg">
      <div className="space-y-0.5">
        <Label className="text-sm font-medium">Force Agtech Sync</Label>
        <p className="text-xs text-muted-foreground">Force one sync to agtech. This will update after the latest pull.</p>
      </div>
      {isUpdating ? (
        <Loader className="w-4 h-4" />
      ) : (
        <Switch
          checked={flag.flag_status}
          onCheckedChange={onUpdateFlag}
          disabled={isUpdating}
        />
      )}
    </div>
  )

}
